// Meeting slots as the calendar server hands them out: UTC instants plus the owner's zone. Labels
// are built here exactly as server/calendar/time.ts `slotLabel` builds them ("Tue 6 Oct,
// 14:00–14:30", fixed English words, the owner's zone) so a card, the approve strip and a receipt
// all name a time the same way. Pure: callers pass the zone (tests pin it).

export interface SlotLike {
  start: string;
  end: string;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat | null {
  const cached = formatters.get(timeZone);
  if (cached) return cached;
  try {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      weekday: "short",
    });
    formatters.set(timeZone, f);
    return f;
  } catch {
    return null;
  }
}

interface Parts {
  weekday: string;
  day: number;
  month: number;
  hour: number;
  minute: number;
}

function partsOf(ms: number, timeZone: string): Parts | null {
  const f = formatter(timeZone);
  if (!f) return null;
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(ms))) if (p.type !== "literal") out[p.type] = p.value;
  return {
    weekday: out.weekday ?? "",
    day: Number(out.day),
    month: Number(out.month),
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
  };
}

const two = (n: number) => String(n).padStart(2, "0");
const clock = (p: Parts) => `${two(p.hour)}:${two(p.minute)}`;

/** "Tue 6 Oct" in `timeZone`; "" for an unreadable instant or zone. */
export function slotDay(start: string, timeZone: string): string {
  const ms = Date.parse(start);
  if (Number.isNaN(ms)) return "";
  const p = partsOf(ms, timeZone);
  if (!p) return "";
  const weekday = WEEKDAYS.find((w) => p.weekday.startsWith(w)) ?? p.weekday;
  return `${weekday} ${p.day} ${MONTHS[p.month - 1] ?? ""}`.trim();
}

/** "14:00–14:30" in `timeZone`; "" when unreadable. */
export function slotTimes(slot: SlotLike, timeZone: string): string {
  const s = Date.parse(slot.start);
  const e = Date.parse(slot.end);
  if (Number.isNaN(s) || Number.isNaN(e)) return "";
  const ps = partsOf(s, timeZone);
  const pe = partsOf(e, timeZone);
  if (!ps || !pe) return "";
  return `${clock(ps)}–${clock(pe)}`;
}

/** "Tue 6 Oct, 14:00–14:30" exactly as the server labels it. */
export function slotLabel(slot: SlotLike, timeZone: string): string {
  const day = slotDay(slot.start, timeZone);
  const times = slotTimes(slot, timeZone);
  return day && times ? `${day}, ${times}` : "";
}

/** Same window (instants compared, so `…00Z` equals `…00.000Z`). */
export function sameSlot(a: SlotLike, b: SlotLike): boolean {
  return Date.parse(a.start) === Date.parse(b.start) && Date.parse(a.end) === Date.parse(b.end);
}

/** Whole minutes from start to end; 0 when unreadable. */
export function slotMinutes(slot: SlotLike): number {
  const ms = Date.parse(slot.end) - Date.parse(slot.start);
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60_000) : 0;
}

/** The reader's IANA zone, or null when the browser cannot say. */
export function viewerZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** "Times in Europe/London" when the slots' zone is not the reader's; null when it is. */
export function zoneNote(
  slotZone: string | null | undefined,
  viewer: string | null,
): string | null {
  if (!slotZone || !viewer || slotZone === viewer) return null;
  return `Times in ${slotZone.replace(/_/g, " ")}`;
}

/** The zone every slot shares, or the first one (a block always comes from one calendar). */
export function zoneOf(slots: readonly { tz: string }[]): string | null {
  return slots[0]?.tz ?? null;
}
